import { useContext, useRef, useState } from "react";
import ToastContext from "../../context/toastProvider";
import Button from "../atoms/button";
import { getArtLabelUploadUrl, postArtLabelCorrection } from "./functions";
import { uploadPng } from "./uploadPng";
import { readPngSize } from "./pngSize";
import { buildEditKit, downloadBlob } from "./editKit";
import { labelCanvas } from "./artLabelPreviewMath";

const NETWORK_RE = /failed to fetch|networkerror|network request failed|load failed/i;

/** Mensagem pt-BR para erro do backend (por code) ou de rede; nunca repassa texto em inglês. */
const friendlyError = (source, fallback) => {
    if (source?.code === "INVALID_UPLOAD") return `Arte corrigida recusada pelo servidor: envie um PNG de ${source.canvasLabel} px gerado a partir do pacote (cada arquivo só pode ser enviado uma vez)`;
    if (source?.code === "INVALID_TRANSITION") return "Não é possível enviar a arte corrigida enquanto há geração em andamento; aguarde terminar";
    if (source instanceof TypeError || NETWORK_RE.test(source?.message || "")) return "Falha de conexão; verifique a internet e tente de novo";
    return fallback;
};

/** Fundo sem personagem: o da geração da variação ou, se ela não tiver (abstrato, correção), o do lote mais novo que tenha. */
export const backgroundFor = (doc, gen) => gen?.inputs?.backgroundUrl
    || [...(doc?.generations || [])].reverse().find((g) => g.inputs?.backgroundUrl)?.inputs.backgroundUrl
    || null;

/**
 * Bloco "Editar fora do sistema" (spec 2026-10-01 §6, R1/R2): qualquer operador baixa o pacote,
 * edita e devolve um PNG do mesmo tamanho, que entra como nova variação (não aprova sozinho).
 */
const EditOutsidePanel = ({ token, label, selected, geometry, reload, disabled = false, onCorrectionAdded }) => {
    const { goodToast, badToast } = useContext(ToastContext);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const canvas = labelCanvas(geometry);
    const blocked = disabled || busy;
    const selectedGen = selected && (label?.generations || []).find((g) => g.id === selected.generationId);
    const selectedOut = selectedGen && (selectedGen.outputs || [])[selected.outputIndex];
    // Tamanho do que se edita: o canvas da etiqueta (continuação nova recorta 1024 × 497), senão o
    // da própria saída, senão o canvas composto. Só a continuação antiga (saída cheia) fica bloqueada.
    const selectedCanvas = selectedGen?.inputs?.labelCanvas
        || (selectedOut?.width && selectedOut?.height ? { width: selectedOut.width, height: selectedOut.height } : null)
        || selectedGen?.inputs?.canvas;
    const legacySize = Boolean(selectedCanvas && (selectedCanvas.width !== canvas.width || selectedCanvas.height !== canvas.height));
    const legacyHint = "Variação do método legado tem outro tamanho; gere de novo para editar fora";
    const withCanvas = (res) => ({ ...res, canvasLabel: `${canvas.width} × ${canvas.height}` });

    const download = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            // URLs assinadas valem 10 min e a revisão não faz polling em `em revisão`: recarrega antes.
            const doc = (await reload()) || label;
            const gen = (doc.generations || []).find((g) => g.id === selected.generationId);
            const out = gen && (gen.outputs || [])[selected.outputIndex];
            if (!out?.url) throw new Error("Variação indisponível para download");
            const kit = await buildEditKit({
                art: doc,
                variation: { url: out.url, width: out.width, height: out.height, method: gen.method, ...(out.fullUrl ? { fullUrl: out.fullUrl } : {}) },
                backgroundUrl: backgroundFor(doc, gen),
                cardImageUrl: doc.sourceImage?.signedUrl || doc.sourceImage?.url || null,
                geometry,
            });
            downloadBlob(kit.blob, kit.name);
            goodToast(`Pacote baixado: ${kit.files.length} arquivos`);
        } catch (err) {
            badToast(friendlyError(err, null) || err.message || "Falha ao montar o pacote");
        } finally {
            setBusy(false);
        }
    };

    const sendCorrection = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        setBusy(true);
        try {
            const size = await readPngSize(file);
            if (!size) throw new Error("O arquivo não é um PNG válido");
            if (size.width !== canvas.width || size.height !== canvas.height) {
                throw new Error(`A arte corrigida precisa ter ${canvas.width} × ${canvas.height} px; este arquivo tem ${size.width} × ${size.height} px`);
            }
            const pre = await getArtLabelUploadUrl(token, label.artKey);
            if (pre?.type !== "success") throw new Error(friendlyError(withCanvas(pre), "Falha ao pedir URL de upload"));
            await uploadPng(pre.data.uploadUrl, file);
            const out = await postArtLabelCorrection(token, label.artKey, pre.data.s3Key);
            if (out?.type !== "success") throw new Error(friendlyError(withCanvas(out), "Falha ao registrar a arte corrigida"));
            goodToast("Arte corrigida enviada como nova variação");
            await onCorrectionAdded(out.data?.generationId);
        } catch (err) {
            badToast(friendlyError(err, null) || err.message);
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div data-testid="edit-outside" className="border border-[#444] p-2 mt-3">
            <p className="text-[#d59106]">Editar fora do sistema</p>
            <p className="text-xs text-gray-400">
                Baixe o pacote, edite a variação num editor de imagem e envie o PNG de {canvas.width} × {canvas.height} px.
                A arte corrigida entra como nova variação e passa pelo checklist antes de aprovar.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
                <Button classes="bg-purple-400 hover:bg-purple-600" click={download} disabled={blocked || !selected || legacySize} title={legacySize ? legacyHint : undefined}>Baixar para editar</Button>
                <label className={`bg-[#ffedd5] text-black px-2 ${blocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                    Enviar arte corrigida
                    <input ref={fileRef} aria-label="Enviar arte corrigida" type="file" accept="image/png" className="sr-only" onChange={sendCorrection} disabled={blocked} />
                </label>
            </div>
        </div>
    );
};

export default EditOutsidePanel;
